export const COST_CATEGORIES = [
  'property_tax', 'water', 'sewage', 'heating', 'hot_water', 'elevator', 'street_cleaning', 'waste',
  'building_cleaning', 'pest_control', 'garden', 'common_electricity', 'chimney', 'insurance',
  'caretaker', 'antenna_cable', 'laundry', 'other_operating',
] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];

/** German labels (BetrKV § 2). UI texts live in i18n; these are used for PDFs and exports. */

export const ALLOCATION_KEYS = [
  'AREA_DAYS', 'PERSON_DAYS', 'UNIT_DAYS', 'CONSUMPTION', 'METERING_RESULT', 'DIRECT', 'CO_OWNERSHIP_SHARE',
] as const;
export type AllocationKey = (typeof ALLOCATION_KEYS)[number];

export const MEMBER_ROLES = ['owner', 'editor', 'caretaker', 'readonly'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/** "Anrede gegenüber Mietern": how tenant-facing documents (statement, letters, e-mails) address the tenant. Default Sie. */
export const TENANT_ADDRESS_FORMS = ['sie', 'du'] as const;
export type TenantAddressForm = (typeof TENANT_ADDRESS_FORMS)[number];

export const PROPERTY_TYPES = ['mfh', 'efh', 'weg_unit', 'mixed', 'commercial'] as const;
export const HEATING_TYPES = ['central_gas', 'central_oil', 'district', 'heatpump', 'floor_units', 'other'] as const;
export const METERING_PROVIDERS = ['ista', 'techem', 'brunata', 'minol', 'kalo', 'other'] as const;
export const UNIT_KINDS = ['apartment', 'commercial', 'parking', 'storage'] as const;
export type UnitKind = (typeof UNIT_KINDS)[number];
export const UNIT_STATUSES = ['rentable', 'owner_occupied', 'renovation', 'not_rentable'] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];
export const TENANCY_KINDS = ['residential', 'commercial', 'shared_flat'] as const;
export const METER_TYPES = ['cold_water', 'warm_water', 'heat', 'hca', 'electricity', 'gas'] as const;
export const METER_UNITS = ['m3', 'kWh', 'units'] as const;
export const READING_SOURCES = ['owner', 'tenant', 'caretaker', 'provider', 'import'] as const;
export const READING_STATUSES = ['proposed', 'confirmed', 'rejected'] as const;
/** Notes of readings written by meter events (installation, removal, swap): their date is fixed by the meter. */
export const METER_EVENT_READING_NOTES = ['Einbau', 'Ausbau', 'Ausbau (Zählertausch)', 'Einbau (Zählertausch)'] as const;
/** Rent changes of a tenancy (packages/shared/src/rentChange.ts explains each kind). */
export const RENT_CHANGE_KINDS = ['agreement', 'increase_558', 'prepayment_560', 'graduated_557a', 'index_557b'] as const;
export type RentChangeKind = (typeof RENT_CHANGE_KINDS)[number];
export const RENT_CHANGE_STATUSES = ['pending_consent', 'applied', 'declined', 'withdrawn'] as const;
export type RentChangeStatus = (typeof RENT_CHANGE_STATUSES)[number];
/** How a rent change letter reached the tenants; every dispatch is logged on the rent change. */
export const RENT_CHANGE_CHANNELS = ['download', 'print', 'email', 'app'] as const;
export type RentChangeChannel = (typeof RENT_CHANGE_CHANNELS)[number];
export const DOCUMENT_KINDS = ['invoice', 'contract', 'protocol', 'tax_notice', 'metering_result', 'other'] as const;
export const DOCUMENT_CHANNELS = ['web', 'mobile', 'mail', 'share', 'import'] as const;
export const DOCUMENT_STATUSES = ['uploaded', 'processing', 'needs_review', 'ready', 'failed', 'duplicate', 'confirmed'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];
/**
 * Why a receipt skipped the AI and waits for manual entry: no consent of the uploader, AI not set up, the AI spend
 * limit reached (analyse again later), a file the AI cannot read (HEIC, CSV/XML, too large), or an incomplete AI
 * answer (cut off or declined).
 */
export const AI_SKIP_REASONS = ['no_consent', 'not_configured', 'limit', 'unsupported_file', 'incomplete'] as const;
export type AiSkipReason = (typeof AI_SKIP_REASONS)[number];
export const APPORTIONABLE = ['yes', 'partial', 'no'] as const;
export const COST_ORIGINS = ['document', 'manual', 'import'] as const;
export const OVERRIDE_LEVELS = ['cost_total', 'key_value', 'recipient_total'] as const;
export type OverrideLevel = (typeof OVERRIDE_LEVELS)[number];
export const PERIOD_STATUSES = ['collecting', 'ready', 'draft', 'final', 'sent', 'closed'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];
export const STATEMENT_STATUSES = ['draft', 'final', 'sent'] as const;
export const DELIVERY_CHANNELS = ['email', 'app', 'letter', 'print'] as const;
export const LEDGER_TYPES = [
  'charge_rent', 'charge_prepayment', 'payment', 'statement_balance', 'dunning_fee', 'deposit', 'correction', 'opening_balance',
] as const;
export type LedgerType = (typeof LEDGER_TYPES)[number];
/** Where an opening balance comes from (docs/15 #58): taken over from the time before ImmoAktas („Übernahme“). */
export const LEDGER_ORIGINS = ['takeover'] as const;
export const BANK_MATCH_STATUSES = ['unmatched', 'suggested', 'matched', 'ignored'] as const;
export const REPORT_CATEGORIES = ['damage', 'question', 'other'] as const;
export const REPORT_STATUSES = ['open', 'in_progress', 'done'] as const;

export const EVENT_ENTITIES = ['property', 'unit', 'tenancy', 'person', 'meter', 'cost_item', 'statement'] as const;
export type EventEntity = (typeof EVENT_ENTITIES)[number];
export const EVENT_TYPES = [
  'unit.created', 'unit.backdated', 'unit.area_changed', 'unit.heating_area_changed', 'unit.status_changed', 'unit.merged', 'unit.split',
  'property.total_area_changed',
  'tenancy.started', 'tenancy.notice_given', 'tenancy.ended', 'tenancy.party_added', 'tenancy.party_removed',
  'tenancy.occupants_changed', 'tenancy.rent_changed', 'tenancy.prepayment_changed', 'tenancy.deposit_received', 'tenancy.unit_corrected',
  'person.address_changed', 'person.deceased',
  'meter.installed', 'meter.replaced', 'meter.removed',
  'statement.finalized', 'statement.sent', 'statement.corrected',
  'handover.protocol',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const ACTOR_KINDS = ['user', 'tenant', 'system', 'admin'] as const;
/** `via: 'agent'`: written by the KI-Assistent for this person (`ref` = proposal id). */
export type Actor = { kind: (typeof ACTOR_KINDS)[number]; id: string; name: string; via?: 'agent'; ref?: string };

